class ShopCategorySlider extends HTMLElement {
  connectedCallback() {
    this._slider = this.querySelector('.sbc-slider');
    this._indicator = this.querySelector('.sbc-progress-indicator');
    this._track = this._indicator?.parentElement;

    if (!this._slider || !this._indicator || !this._track) return;

    this._onScroll = this._updateProgress.bind(this);
    this._onMouseMove = this._handleMouseMove.bind(this);
    this._onMouseUp = this._handleMouseUp.bind(this);
    this._onTouchMove = this._handleTouchMove.bind(this);
    this._onTouchEnd = this._handleTouchEnd.bind(this);

    this._slider.addEventListener('scroll', this._onScroll, { passive: true });
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);
    document.addEventListener('touchmove', this._onTouchMove, { passive: true });
    document.addEventListener('touchend', this._onTouchEnd);

    this._initSliderDrag();
    this._initIndicatorDrag();
    this._updateProgress();
    this._checkOverflow();
    this._resizeObserver = new ResizeObserver(() => this._checkOverflow());
    this._resizeObserver.observe(this._slider);
  }

  disconnectedCallback() {
    this._slider?.removeEventListener('scroll', this._onScroll);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    document.removeEventListener('touchmove', this._onTouchMove);
    document.removeEventListener('touchend', this._onTouchEnd);
    this._resizeObserver?.disconnect();
  }

  _checkOverflow() {
    const hasOverflow = this._slider.scrollWidth > this._slider.clientWidth + 1;
    this.classList.toggle('sbc-slider--no-scroll', !hasOverflow);
  }

  _updateProgress() {
    const maxScroll = this._slider.scrollWidth - this._slider.clientWidth;
    if (maxScroll <= 0) {
      this._indicator.style.width = '100%';
      this._indicator.style.left = '0%';
      return;
    }
    const isMobile = window.innerWidth <= 749;
    const maxIndicatorPx = isMobile ? 42 : 379;
    const trackW = this._track.clientWidth;
    const visibleRatio = this._slider.clientWidth / this._slider.scrollWidth;
    const indicatorPx = Math.min(Math.max(visibleRatio * trackW, trackW * 0.08), maxIndicatorPx);
    const indicatorW = (indicatorPx / trackW) * 100;
    const progress = this._slider.scrollLeft / maxScroll;
    this._indicator.style.width = indicatorW + '%';
    this._indicator.style.left = progress * (100 - indicatorW) + '%';
  }

  // --- Slider drag ---
  _initSliderDrag() {
    this._sliderDragging = false;
    this._didDrag = false;

    this._slider.addEventListener('mousedown', (e) => {
      this._sliderDragging = true;
      this._didDrag = false;
      this._sliderStartX = e.pageX;
      this._sliderScrollStart = this._slider.scrollLeft;
      this._slider.classList.add('is-dragging');
      e.preventDefault();
    });

    // Prevent click-triggered navigation after a real drag (e.g. anchor cards scrolling to top)
    this._slider.addEventListener('click', (e) => {
      if (this._didDrag) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  _handleMouseMove(e) {
    if (this._sliderDragging) {
      const dx = e.pageX - this._sliderStartX;
      if (Math.abs(dx) > 5) this._didDrag = true;
      this._slider.scrollLeft = this._sliderScrollStart - dx;
    }
    if (this._indicatorDragging) {
      this._moveIndicator(e.clientX);
    }
  }

  _handleMouseUp() {
    if (this._sliderDragging) {
      this._sliderDragging = false;
      this._slider.classList.remove('is-dragging');
      // Reset after click has had a chance to fire
      setTimeout(() => { this._didDrag = false; }, 0);
    }
    if (this._indicatorDragging) {
      this._indicatorDragging = false;
      this._indicator.style.transition = '';
    }
  }

  // --- Indicator drag ---
  _initIndicatorDrag() {
    this._indicatorDragging = false;

    this._indicator.addEventListener('mousedown', (e) => {
      this._startIndicatorDrag(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    });

    this._indicator.addEventListener('touchstart', (e) => {
      this._startIndicatorDrag(e.touches[0].clientX);
      e.stopPropagation();
    }, { passive: true });
  }

  _startIndicatorDrag(clientX) {
    this._indicatorDragging = true;
    this._indicatorStartX = clientX;
    this._indicatorScrollStart = this._slider.scrollLeft;
    this._indicator.style.transition = 'none';
  }

  _moveIndicator(clientX) {
    const maxScroll = this._slider.scrollWidth - this._slider.clientWidth;
    if (maxScroll <= 0) return;
    const availablePx = this._track.clientWidth - this._indicator.offsetWidth;
    if (availablePx <= 0) return;
    const dx = clientX - this._indicatorStartX;
    this._slider.scrollLeft = Math.max(0, Math.min(
      this._indicatorScrollStart + (dx / availablePx) * maxScroll,
      maxScroll
    ));
  }

  _handleTouchMove(e) {
    if (this._indicatorDragging) {
      this._moveIndicator(e.touches[0].clientX);
    }
  }

  _handleTouchEnd() {
    if (this._indicatorDragging) {
      this._indicatorDragging = false;
      this._indicator.style.transition = '';
    }
  }
}

if (!customElements.get('sbc-slider')) {
  customElements.define('sbc-slider', ShopCategorySlider);
}
